export type PmsNodeType =
  | 'vessel'
  | 'department'
  | 'system'
  | 'component'
  | 'subcomponent'
  | 'equipment'
  | 'instance'
  | 'job';

export type PmsHierarchySelection = {
  type: PmsNodeType;
  id: number;
};

export type PmsTreeIcon = PmsNodeType;

export type PmsTreeNode = {
  key: string;
  label: string;
  count: number;
  muted?: boolean;
  icon: PmsTreeIcon;
  iconClassName?: string;
  selection: PmsHierarchySelection;
  expandKey?: string;
  children?: PmsTreeNode[];
  /** Direct child rows for the right-pane table */
  childRows?: PmsChildRow[];
};

export type PmsChildRow = {
  id: number;
  type: PmsNodeType;
  code: string;
  name: string;
  isActive?: boolean;
  detailPath: string;
  editPath: string;
  /** Type-specific display fields for the right-pane table */
  fields?: Record<string, string | null | undefined>;
};

export type PmsHierarchyPayload = {
  vessels: Array<{ id: number; vesselName: string; vesselAbrv?: string | null; imoNumber?: string | null }>;
  departments: Array<{
    id: number;
    vesselId: number;
    parentDepartmentId?: number | null;
    code: string;
    name: string;
    displayOrder?: number;
    isActive?: boolean;
  }>;
  systems: Array<{
    id: number;
    vesselId: number;
    departmentId: number;
    parentSystemId?: number | null;
    code: string;
    name: string;
    displayOrder?: number;
    isActive?: boolean;
  }>;
  components: Array<{
    id: number;
    systemId: number;
    vesselId: number;
    departmentId: number;
    parentComponentId?: number | null;
    code: string;
    name: string;
    displayOrder?: number;
    isActive?: boolean;
  }>;
  subComponents: Array<{
    id: number;
    componentId: number;
    vesselId: number;
    departmentId: number;
    systemId: number;
    parentSubComponentId?: number | null;
    code: string;
    name: string;
    displayOrder?: number;
    isActive?: boolean;
  }>;
  equipment: Array<{
    id: number;
    vesselId: number;
    departmentId: number;
    systemId?: number | null;
    componentId?: number | null;
    subComponentId?: number | null;
    parentEquipmentId?: number | null;
    equipmentCode: string;
    name: string;
    isActive?: boolean;
  }>;
  equipmentInstances: Array<{
    id: number;
    equipmentId: number;
    vesselId: number;
    statusId?: number;
    serialNumber?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    partNumber?: string | null;
    installedLocation?: string | null;
    condition?: string | null;
    notes?: string | null;
    previousInstanceId?: number | null;
    equipmentCode?: string | null;
    equipmentName?: string | null;
    status?: { id: number; code?: string | null; name?: string | null } | null;
    previousInstance?: { id: number; serialNumber?: string | null } | null;
  }>;
  maintenanceJobs: Array<{
    id: number;
    equipmentId: number;
    vesselId: number;
    departmentId: number;
    responsibleRankId?: number | null;
    secondaryRankId?: number | null;
    jobCode: string;
    title: string;
    description?: string | null;
    jobTypeId?: number;
    maintenanceStrategyId?: number;
    criticalityId?: number;
    verificationStatusId?: number;
    priority?: number | null;
    requiredPersonnel?: number | null;
    permitRequired?: boolean;
    riskAssessmentRequired?: boolean;
    isActive?: boolean;
    equipmentCode?: string | null;
    equipmentName?: string | null;
    jobType?: { id: number; code?: string | null; name?: string | null } | null;
    maintenanceStrategy?: { id: number; code?: string | null; name?: string | null } | null;
    criticality?: { id: number; code?: string | null; name?: string | null } | null;
    verificationStatus?: { id: number; code?: string | null; name?: string | null } | null;
    department?: { id: number; code?: string | null; name?: string | null } | null;
    responsibleRank?: { id: number; abrv?: string | null; name?: string | null } | null;
    secondaryRank?: { id: number; abrv?: string | null; name?: string | null } | null;
    primarySchedule?: {
      nextDueDate?: string | null;
      nextDueCounter?: number | string | null;
      lastCompletedDate?: string | null;
      lastCompletedCounter?: number | string | null;
      intervalValue?: number | string | null;
      scheduleType?: { code?: string | null; name?: string | null } | null;
      intervalUnit?: { code?: string | null; name?: string | null } | null;
      counter?: { name?: string | null; unit?: string | null } | null;
    } | null;
    dueStatus?: string | null;
  }>;
};

const PATHS: Record<Exclude<PmsNodeType, 'vessel'>, { list: string; base: string }> = {
  department: { list: '/pms/pms-departments', base: '/pms/pms-departments' },
  system: { list: '/pms/pms-equipment-systems', base: '/pms/pms-equipment-systems' },
  component: { list: '/pms/pms-components', base: '/pms/pms-components' },
  subcomponent: { list: '/pms/pms-sub-components', base: '/pms/pms-sub-components' },
  equipment: { list: '/pms/pms-equipment', base: '/pms/pms-equipment' },
  instance: { list: '/pms/pms-equipment-instances', base: '/pms/pms-equipment-instances' },
  job: { list: '/pms/pms-maintenance-jobs', base: '/pms/pms-maintenance-jobs' },
};

export function detailPathFor(type: PmsNodeType, id: number): string {
  if (type === 'vessel') return `/parameters/vessels/${id}`;
  return `${PATHS[type].base}/${id}`;
}

export function editPathFor(type: PmsNodeType, id: number): string {
  if (type === 'vessel') return `/parameters/vessels/${id}/edit`;
  return `${PATHS[type].base}/${id}/edit`;
}

export function newPathFor(
  type: Exclude<PmsNodeType, 'vessel'>,
  parent?: PmsHierarchySelection | null,
  vesselId?: number,
): string {
  const qs = new URLSearchParams();
  if (vesselId != null) qs.set('vesselId', String(vesselId));
  if (parent) {
    if (parent.type === 'vessel') qs.set('vesselId', String(parent.id));
    if (parent.type === 'department') qs.set('departmentId', String(parent.id));
    if (parent.type === 'system') qs.set('systemId', String(parent.id));
    if (parent.type === 'component') qs.set('componentId', String(parent.id));
    if (parent.type === 'subcomponent') qs.set('subComponentId', String(parent.id));
    if (parent.type === 'equipment') {
      if (type === 'equipment') qs.set('parentEquipmentId', String(parent.id));
      else qs.set('equipmentId', String(parent.id));
    }
  }
  const q = qs.toString();
  return `${PATHS[type].base}/new${q ? `?${q}` : ''}`;
}

function codeName(
  rel: { code?: string | null; name?: string | null; abrv?: string | null } | null | undefined,
  fallbackId?: number | null,
): string {
  if (!rel && fallbackId == null) return '-';
  if (rel?.name) return rel.name;
  return rel?.abrv ?? rel?.code ?? (fallbackId != null ? `#${fallbackId}` : '-');
}

function yn(v?: boolean | null) {
  if (v == null) return '-';
  return v ? 'Yes' : 'No';
}

function instanceChildRow(i: PmsHierarchyPayload['equipmentInstances'][number]): PmsChildRow {
  const code = i.serialNumber?.trim() || `#${i.id}`;
  const name = [i.manufacturer, i.model].filter(Boolean).join(' ') || 'Instance';
  return {
    id: i.id,
    type: 'instance',
    code,
    name,
    detailPath: detailPathFor('instance', i.id),
    editPath: editPathFor('instance', i.id),
    fields: {
      serialNumber: i.serialNumber ?? '-',
      status: codeName(i.status, i.statusId),
      manufacturer: i.manufacturer ?? '-',
      model: i.model ?? '-',
      partNumber: i.partNumber ?? '-',
      installedLocation: i.installedLocation ?? '-',
      condition: i.condition ?? '-',
      previousInstance: i.previousInstance?.serialNumber ?? (i.previousInstanceId != null ? `#${i.previousInstanceId}` : '-'),
      notes: i.notes ?? '-',
    },
  };
}

function jobChildRow(j: PmsHierarchyPayload['maintenanceJobs'][number]): PmsChildRow {
  const s = j.primarySchedule;
  const freq =
    s?.intervalValue != null
      ? `${s.intervalValue}${s.intervalUnit ? ` ${s.intervalUnit.name ?? s.intervalUnit.code}` : ''}`
      : '-';
  let nextDue = '-';
  if (s?.nextDueDate) nextDue = String(s.nextDueDate).slice(0, 10);
  else if (s?.nextDueCounter != null) {
    nextDue = `${s.nextDueCounter}${s.counter?.unit ? ` ${s.counter.unit}` : ''}`;
  }
  let lastDone = '-';
  if (s?.lastCompletedDate) lastDone = String(s.lastCompletedDate).slice(0, 10);
  else if (s?.lastCompletedCounter != null) lastDone = String(s.lastCompletedCounter);

  return {
    id: j.id,
    type: 'job',
    code: '',
    name: j.title,
    isActive: j.isActive,
    detailPath: detailPathFor('job', j.id),
    editPath: editPathFor('job', j.id),
    fields: {
      title: j.title,
      description: j.description ?? '-',
      jobType: codeName(j.jobType, j.jobTypeId),
      strategy: codeName(j.maintenanceStrategy, j.maintenanceStrategyId),
      criticality: codeName(j.criticality, j.criticalityId),
      verification: codeName(j.verificationStatus, j.verificationStatusId),
      department: codeName(j.department, j.departmentId),
      responsibleRank: codeName(j.responsibleRank, j.responsibleRankId),
      secondaryRank: codeName(j.secondaryRank, j.secondaryRankId),
      priority: j.priority != null ? String(j.priority) : '-',
      requiredPersonnel: j.requiredPersonnel != null ? String(j.requiredPersonnel) : '-',
      permitRequired: yn(j.permitRequired),
      riskAssessmentRequired: yn(j.riskAssessmentRequired),
      scheduleMode: codeName(s?.scheduleType),
      frequency: freq,
      lastDone,
      nextDue,
      dueStatus: j.dueStatus ?? '-',
      counter: s?.counter?.name ?? '-',
      isActive: j.isActive === false ? 'No' : 'Yes',
    },
  };
}

function nodeKey(type: PmsNodeType, id: number) {
  return `${type}:${id}`;
}

/** Recursive builder with accurate counts and childRows. */
function buildTypedSelfTree<T extends { id: number; code: string; name: string; isActive?: boolean }>(
  items: T[],
  parentIdOf: (item: T) => number | null | undefined,
  type: Exclude<PmsNodeType, 'vessel'>,
  iconClassName: string,
  nestedUnder: (item: T) => { nodes: PmsTreeNode[]; rows: PmsChildRow[] },
): PmsTreeNode[] {
  const byParent = new Map<number | null, T[]>();
  for (const item of items) {
    const pid = parentIdOf(item) ?? null;
    const list = byParent.get(pid) ?? [];
    list.push(item);
    byParent.set(pid, list);
  }

  const walk = (parentId: number | null): PmsTreeNode[] => {
    const list = byParent.get(parentId) ?? [];
    return list.map((item) => {
      const selfKids = walk(item.id);
      const nested = nestedUnder(item);
      const children = [...selfKids, ...nested.nodes];
      const childRows: PmsChildRow[] = [
        ...selfKids.map((n) => ({
          id: n.selection.id,
          type: n.selection.type,
          code: itemCodeFromLabel(n.label),
          name: itemNameFromLabel(n.label),
          isActive: !n.muted,
          detailPath: detailPathFor(n.selection.type, n.selection.id),
          editPath: editPathFor(n.selection.type, n.selection.id),
        })),
        ...nested.rows,
      ];
      // Match detail-pane "N children" (immediate childRows), not leaf rollup only.
      const count = childRows.length;
      return {
        key: nodeKey(type, item.id),
        label: item.name,
        count,
        muted: item.isActive === false,
        icon: type,
        iconClassName,
        selection: { type, id: item.id },
        expandKey: children.length ? nodeKey(type, item.id) : undefined,
        children: children.length ? children : undefined,
        childRows: childRows.length ? childRows : undefined,
      };
    });
  };

  return walk(null);
}

function itemCodeFromLabel(label: string) {
  const i = label.indexOf(' — ');
  return i >= 0 ? label.slice(0, i) : '';
}

function itemNameFromLabel(label: string) {
  const i = label.indexOf(' — ');
  return i >= 0 ? label.slice(i + 3) : label;
}

function equipmentNodes(
  items: PmsHierarchyPayload['equipment'],
  parentEquipmentId: number | null,
  filter: (e: PmsHierarchyPayload['equipment'][number]) => boolean,
  instances: PmsHierarchyPayload['equipmentInstances'],
  jobs: PmsHierarchyPayload['maintenanceJobs'],
): { nodes: PmsTreeNode[]; rows: PmsChildRow[] } {
  const filtered = items.filter(filter);
  const byParent = new Map<number | null, typeof filtered>();
  for (const e of filtered) {
    const pid = e.parentEquipmentId ?? null;
    const list = byParent.get(pid) ?? [];
    list.push(e);
    byParent.set(pid, list);
  }

  const instanceRowsFor = (equipmentId: number): PmsChildRow[] =>
    instances.filter((i) => i.equipmentId === equipmentId).map(instanceChildRow);

  const jobRowsFor = (equipmentId: number): PmsChildRow[] =>
    jobs.filter((j) => j.equipmentId === equipmentId).map(jobChildRow);

  const toEquipRow = (n: PmsTreeNode): PmsChildRow => ({
    id: n.selection.id,
    type: n.selection.type,
    code: itemCodeFromLabel(n.label),
    name: itemNameFromLabel(n.label),
    isActive: !n.muted,
    detailPath: detailPathFor(n.selection.type, n.selection.id),
    editPath: editPathFor(n.selection.type, n.selection.id),
  });

  const walk = (pid: number | null): PmsTreeNode[] => {
    const list = byParent.get(pid) ?? [];
    return list.map((e) => {
      // Tree lists child equipment only; detail pane also lists instances + jobs.
      // Tree count uses the same childRows length as the detail "N children" badge.
      const childEquip = walk(e.id);
      const childRows = [
        ...childEquip.map(toEquipRow),
        ...instanceRowsFor(e.id),
        ...jobRowsFor(e.id),
      ];
      return {
        key: nodeKey('equipment', e.id),
        label: e.name,
        count: childRows.length,
        muted: e.isActive === false,
        icon: 'equipment' as const,
        iconClassName: 'text-rose-500 dark:text-rose-400',
        selection: { type: 'equipment' as const, id: e.id },
        expandKey: childEquip.length ? nodeKey('equipment', e.id) : undefined,
        children: childEquip.length ? childEquip : undefined,
        childRows: childRows.length ? childRows : undefined,
      };
    });
  };

  const nodes = walk(parentEquipmentId);
  const rows = nodes.map(toEquipRow);
  return { nodes, rows };
}

export function buildPmsHierarchyTree(data: PmsHierarchyPayload): PmsTreeNode[] {
  const {
    vessels,
    departments,
    systems,
    components,
    subComponents,
    equipment,
    equipmentInstances = [],
    maintenanceJobs = [],
  } = data;

  return vessels.map((v) => {
    const vesselDepts = departments.filter((d) => d.vesselId === v.id);
    const vesselSystems = systems.filter((s) => s.vesselId === v.id);
    const vesselComponents = components.filter((c) => c.vesselId === v.id);
    const vesselSubs = subComponents.filter((s) => s.vesselId === v.id);
    const vesselEquip = equipment.filter((e) => e.vesselId === v.id);
    const vesselInst = equipmentInstances.filter((i) => i.vesselId === v.id);
    const vesselJobs = maintenanceJobs.filter((j) => j.vesselId === v.id);

    const eq = (
      parentEquipmentId: number | null,
      filter: (e: PmsHierarchyPayload['equipment'][number]) => boolean,
    ) => equipmentNodes(vesselEquip, parentEquipmentId, filter, vesselInst, vesselJobs);

    const deptNodes = buildTypedSelfTree(
      vesselDepts,
      (d) => d.parentDepartmentId,
      'department',
      'text-indigo-500 dark:text-indigo-400',
      (dept) => {
        const deptSystems = vesselSystems.filter((s) => s.departmentId === dept.id);
        const systemNodes = buildTypedSelfTree(
          deptSystems,
          (s) => s.parentSystemId,
          'system',
          'text-sky-500 dark:text-sky-400',
          (sys) => {
            const sysComponents = vesselComponents.filter((c) => c.systemId === sys.id);
            const componentNodes = buildTypedSelfTree(
              sysComponents,
              (c) => c.parentComponentId,
              'component',
              'text-emerald-600 dark:text-emerald-400',
              (comp) => {
                const compSubs = vesselSubs.filter((s) => s.componentId === comp.id);
                const subNodes = buildTypedSelfTree(
                  compSubs,
                  (s) => s.parentSubComponentId,
                  'subcomponent',
                  'text-amber-600 dark:text-amber-400',
                  (sub) => eq(null, (e) => e.subComponentId === sub.id),
                );
                const equipHere = eq(
                  null,
                  (e) => e.componentId === comp.id && e.subComponentId == null,
                );
                return {
                  nodes: [...subNodes, ...equipHere.nodes],
                  rows: [
                    ...subNodes.map((n) => ({
                      id: n.selection.id,
                      type: n.selection.type,
                      code: itemCodeFromLabel(n.label),
                      name: itemNameFromLabel(n.label),
                      isActive: !n.muted,
                      detailPath: detailPathFor(n.selection.type, n.selection.id),
                      editPath: editPathFor(n.selection.type, n.selection.id),
                    })),
                    ...equipHere.rows,
                  ],
                };
              },
            );
            const equipOnSystem = eq(
              null,
              (e) => e.systemId === sys.id && e.componentId == null && e.subComponentId == null,
            );
            return {
              nodes: [...componentNodes, ...equipOnSystem.nodes],
              rows: [
                ...componentNodes.map((n) => ({
                  id: n.selection.id,
                  type: n.selection.type,
                  code: itemCodeFromLabel(n.label),
                  name: itemNameFromLabel(n.label),
                  isActive: !n.muted,
                  detailPath: detailPathFor(n.selection.type, n.selection.id),
                  editPath: editPathFor(n.selection.type, n.selection.id),
                })),
                ...equipOnSystem.rows,
              ],
            };
          },
        );
        const equipOnDept = eq(
          null,
          (e) =>
            e.departmentId === dept.id &&
            e.systemId == null &&
            e.componentId == null &&
            e.subComponentId == null,
        );
        return {
          nodes: [...systemNodes, ...equipOnDept.nodes],
          rows: [
            ...systemNodes.map((n) => ({
              id: n.selection.id,
              type: n.selection.type,
              code: itemCodeFromLabel(n.label),
              name: itemNameFromLabel(n.label),
              isActive: !n.muted,
              detailPath: detailPathFor(n.selection.type, n.selection.id),
              editPath: editPathFor(n.selection.type, n.selection.id),
            })),
            ...equipOnDept.rows,
          ],
        };
      },
    );

    const childRows = deptNodes.map((n) => ({
      id: n.selection.id,
      type: n.selection.type,
      code: itemCodeFromLabel(n.label),
      name: itemNameFromLabel(n.label),
      isActive: !n.muted,
      detailPath: detailPathFor(n.selection.type, n.selection.id),
      editPath: editPathFor(n.selection.type, n.selection.id),
    }));
    return {
      key: nodeKey('vessel', v.id),
      label: v.vesselName,
      count: childRows.length,
      icon: 'vessel' as const,
      iconClassName: 'text-indigo-500 dark:text-indigo-400',
      selection: { type: 'vessel' as const, id: v.id },
      expandKey: deptNodes.length ? nodeKey('vessel', v.id) : undefined,
      children: deptNodes.length ? deptNodes : undefined,
      childRows: childRows.length ? childRows : undefined,
    };
  });
}

export function selectionMatches(a: PmsHierarchySelection | null, b: PmsHierarchySelection): boolean {
  return Boolean(a && a.type === b.type && a.id === b.id);
}

export function selectionTitle(tree: PmsTreeNode[], selection: PmsHierarchySelection | null): string {
  if (!selection) return 'Select a node';
  const path: string[] = [];
  const walk = (nodes: PmsTreeNode[], trail: string[]): boolean => {
    for (const n of nodes) {
      const next = [...trail, n.label];
      if (selectionMatches(selection, n.selection)) {
        path.push(...next);
        return true;
      }
      if (n.children && walk(n.children, next)) return true;
    }
    return false;
  };
  walk(tree, []);
  return path.length ? path.join(' · ') : 'Select a node';
}

export function findNode(
  tree: PmsTreeNode[],
  selection: PmsHierarchySelection | null,
): PmsTreeNode | null {
  if (!selection) return null;
  const walk = (nodes: PmsTreeNode[]): PmsTreeNode | null => {
    for (const n of nodes) {
      if (selectionMatches(selection, n.selection)) return n;
      if (n.children) {
        const found = walk(n.children);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(tree);
}

export function coerceSelectionToTree(
  tree: PmsTreeNode[],
  selection: PmsHierarchySelection | null,
): PmsHierarchySelection | null {
  if (!selection) return tree[0]?.selection ?? null;
  if (findNode(tree, selection)) return selection;
  return tree[0]?.selection ?? null;
}

export function expandedKeysForSelection(
  tree: PmsTreeNode[],
  selection: PmsHierarchySelection | null,
): Set<string> {
  const keys = new Set<string>();
  if (!selection) return keys;
  const walk = (nodes: PmsTreeNode[], trail: string[]): boolean => {
    for (const n of nodes) {
      const next = n.expandKey ? [...trail, n.expandKey] : trail;
      if (selectionMatches(selection, n.selection)) {
        next.forEach((k) => keys.add(k));
        return true;
      }
      if (n.children && walk(n.children, next)) {
        next.forEach((k) => keys.add(k));
        return true;
      }
    }
    return false;
  };
  walk(tree, []);
  return keys;
}

export function filterTreeByQuery(nodes: PmsTreeNode[], query: string): PmsTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;

  const filter = (list: PmsTreeNode[]): PmsTreeNode[] => {
    const out: PmsTreeNode[] = [];
    for (const n of list) {
      const selfMatch = n.label.toLowerCase().includes(q);
      const kids = n.children ? filter(n.children) : [];
      if (selfMatch || kids.length) {
        out.push({
          ...n,
          children: selfMatch ? n.children : kids.length ? kids : undefined,
          expandKey: selfMatch || kids.length ? n.expandKey ?? n.key : n.expandKey,
        });
      }
    }
    return out;
  };
  return filter(nodes);
}

export function collectExpandKeys(nodes: PmsTreeNode[]): Set<string> {
  const keys = new Set<string>();
  const walk = (list: PmsTreeNode[]) => {
    for (const n of list) {
      if (n.expandKey && n.children?.length) keys.add(n.expandKey);
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return keys;
}

/** Suggested child type to create under the current selection */
export function suggestedChildType(selection: PmsHierarchySelection | null): Exclude<PmsNodeType, 'vessel'> | null {
  if (!selection) return 'department';
  switch (selection.type) {
    case 'vessel':
      return 'department';
    case 'department':
      return 'system';
    case 'system':
      return 'component';
    case 'component':
      return 'subcomponent';
    case 'subcomponent':
      return 'equipment';
    case 'equipment':
      return 'instance';
    case 'instance':
    case 'job':
      return null;
    default:
      return null;
  }
}

/** Extra create actions when equipment is selected (instance is primary via suggestedChildType). */
export function equipmentExtraChildTypes(): Array<'job' | 'equipment'> {
  return ['job', 'equipment'];
}

export const CHILD_TYPE_LABELS: Record<Exclude<PmsNodeType, 'vessel'>, string> = {
  department: 'Department',
  system: 'Equipment system',
  component: 'Component',
  subcomponent: 'Sub-component',
  equipment: 'Equipment',
  instance: 'Equipment instance',
  job: 'Maintenance job',
};

export const NODE_TYPE_LABELS: Record<PmsNodeType, string> = {
  vessel: 'Vessel',
  department: 'Department',
  system: 'Equipment system',
  component: 'Component',
  subcomponent: 'Sub-component',
  equipment: 'Equipment',
  instance: 'Instance',
  job: 'Job',
};
